import { notification } from 'antd';
import MessageService from 'services/MessageService';
import { MessageTopic } from 'data/Message';
import moment, { Moment } from 'moment';
import { Rule } from 'antd/lib/form';
import { AccountServiceInfo, ServiceResponse, DefaultDisplayDateFormat, DefaultJsonDateFormat, HCaptchaTokenHeaderName, ImpersonateTokenName, ListSelfReadUpload, MeterDto, MeterType, SelfReadReview, SelfReadUploadReview, Site, VerificationCodePurpose, VerificationCodeType, EuropaAccountServiceStatus, AccountMeterDto, ServiceType, StateCode, ContactTitle, DeliveryType, SelfReadReason, SelfReadReviewReason, SelfReadUploadReviewReason } from 'data/CustomerPortalTypes';
import { ChangeEvent, CSSProperties, FocusEvent, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faFire } from '@fortawesome/free-solid-svg-icons';
import { ParsedQuery, stringify } from 'query-string';
import { JsonHeader } from './HttpHelper';
import { isHoliday } from './DateTool';

enum MonthNameNumDic {
    Jan = '01',
    Feb = '02',
    Mar = '03',
    Apr = '04',
    May = '05',
    Jun = '06',
    Jul = '07',
    Aug = '08',
    Sep = '09',
    Oct = '10',
    Nov = '11',
    Dec = '12'
}

const AllStateCodes = enumerateEnumVal<StateCode>(StateCode);
const AllContactTitles = enumerateEnumVal<ContactTitle>(ContactTitle);
const AllDeliveryTypes = enumerateEnumVal<DeliveryType>(DeliveryType);
const AllSelfReadReasons = enumerateEnumVal<SelfReadReason>(SelfReadReason);
const AllSelfReadReviewReason = enumerateEnumVal<SelfReadReviewReason>(SelfReadReviewReason);
const AllSelfReadUploadReviewReason = enumerateEnumVal<SelfReadUploadReviewReason>(SelfReadReviewReason);

const allMonthNames = enumerateEnumKey(MonthNameNumDic);

const singleDigitRegex = /([^\d]|^)(\d)([^\d]|$)/g;
const prefixZeroReplStr = '$10$2$3';

const formatDateMoment = (targetDate: Date | null | undefined | Moment | string): Moment | null => {
    if (!targetDate) {
        return null;
    }

    const result = moment(targetDate);

    if (result.isValid()) {
        return moment(
            result.format(DefaultJsonDateFormat),
            DefaultJsonDateFormat);
    }

    if (typeof targetDate === 'string') {

        allMonthNames.forEach(mn =>
            targetDate = (targetDate as string).replace(new RegExp(mn, 'ig'), MonthNameNumDic[mn]));

        let temp = moment(targetDate);

        if (!temp.isValid()) {
            let loopCount = 0;

            while (singleDigitRegex.test(targetDate) && loopCount++ < 5) {
                targetDate = (targetDate as string).replace(singleDigitRegex, prefixZeroReplStr);
            }

            temp = moment(targetDate);
        }

        if (temp.isValid()) {
            return moment(
                temp.format(DefaultJsonDateFormat),
                DefaultJsonDateFormat); // keep the date part only
        }
    }

    return null;
}

const formatDisplayDate = (targetDate: Parameters<typeof formatDateMoment>[0]): string =>
    formatDateMoment(targetDate)?.format(DefaultDisplayDateFormat) ?? '';

const formatJsonDate = (targetDate: Parameters<typeof formatDateMoment>[0]): string | null =>
    formatDateMoment(targetDate)?.format(DefaultJsonDateFormat) ?? null;

const formatTime = (targetDate: Date | null | undefined): string => {
    if (!targetDate) {
        return '';
    }

    return moment(targetDate).format("HH:mm");
}

const getDateFromString = (dateStr: string): Date => {
    return moment(dateStr).toDate();
}

const getUTCDate = (date: Date): Date => {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()))
}

const convertObjectDate = (
    target: any,
    keyTrack?: any
) => {
    keyTrack ??= {};

    if (!target) {
        return;
    }

    for (let field of Object.keys(target)) {
        if (typeof (target[field]) == "object") {

            if (keyTrack[target[field]]) { // avoid dead loop
                continue;
            }

            keyTrack[target[field]] = true;

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            convertObjectDate(target[field], keyTrack);
        }
        else {
            let dateVal = Date.parse(target[field]);
            if (dateVal > 0) {
                target[field] = new Date(dateVal);
            }
        }
    }

    return target;
}

const cloneObject = (target: any) => {
    if (!target) {
        return;
    }

    let clone: any = JSON.parse(JSON.stringify(target));

    return clone;
}

const isMobileAndTablet = () => {
    let check = false;
    (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substring(0, 4))) check = true; })(navigator.userAgent || navigator.vendor);
    return check;
};

const startSpinner = () => {
    MessageService.get().send({
        topic: MessageTopic.requestStart,
        data: null
    });
}

const stopSpinner = () => {
    MessageService.get().send({
        topic: MessageTopic.requestEnd,
        data: null
    });
}


const handleResponse = async <T,>(
    responsePromise: Promise<Response>,
    enableSpin: boolean = true,
    enableErrorToast: boolean = true,
    redirectCb?: () => void,
    showGenericErrorMessage: boolean = false): Promise<ServiceResponse<T> | null> => {

    var errorMessage = '';
    let response: ServiceResponse<T> | null = null;

    try {
        enableSpin && MessageService.get().send({
            topic: MessageTopic.requestStart,
            data: null
        });

        const responseData = await responsePromise;

        if (responseData.redirected) {
            redirectCb?.();
            window.location.replace(`${responseData.url}`);
        }

        if (responseData.status < 400) {
            response = await responseData.json() as ServiceResponse<T>;

            if (response?.success !== true) {
                errorMessage += 'Request failed: ';
                response?.message && (errorMessage += response.message);
            }
        }
        else {
            errorMessage += `Request failed with\nStatus Code: ${responseData.status}`;

            try {
                const body = await responseData.text();

                body && (errorMessage += '\nMessage: ' + body)
            } catch { }
        }
    } catch {
        errorMessage += 'Unable to send request.';
    }

    enableSpin && MessageService.get().send({
        topic: MessageTopic.requestEnd,
        data: null
    });

    if (errorMessage) {
        if (showGenericErrorMessage) {
            errorMessage = 'Something went wrong. Please try again later.';
        }

        enableErrorToast
        && showErrorToast(errorMessage);
    }

    return response;
}


const contentDispositionRegex = /filename=(?<fileName>[^;]*)(;|$)/;

const handleDownlodResponse = async <T,>(
    responsePromise: Promise<Response>,
    enableSpin: boolean = true,
    enableErrorToast: boolean = true,
    showGenericErrorMessage: boolean = false) => {

    var errorMessage = '';
    let response: ServiceResponse<T> | null = null;

    try {
        enableSpin && MessageService.get().send({
            topic: MessageTopic.requestStart,
            data: null
        });

        const responseData = await responsePromise;

        if (responseData.status < 400) {
            const blob = await responseData.blob();

            const a = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            const contentDisposition = responseData.headers.get('Content-Disposition');

            let fileName = "downlod.csv";
            if (contentDisposition) {
                const matched = contentDispositionRegex.exec(contentDisposition);

                if (matched?.groups?.['fileName']) {
                    fileName = matched?.groups?.['fileName'];
                    fileName = fileName.replace(/^"|"$/g, '')
                }
            }

            a.href = url;
            a.download = fileName;
            a.click();

            window.URL.revokeObjectURL(url);
        }
        else {
            errorMessage += `Request failed with\nStatus Code: ${responseData.status}`;

            try {
                const body = await responseData.text();

                body && (errorMessage += '\nMessage: ' + body)
            } catch { }
        }
    } catch {
        errorMessage += 'Unable to send request.';
    }

    enableSpin && MessageService.get().send({
        topic: MessageTopic.requestEnd,
        data: null
    });

    if (errorMessage) {
        if (showGenericErrorMessage) {
            errorMessage = 'Something went wrong. Please try again later.';
        }

        enableErrorToast
        && showErrorToast(errorMessage);
    }

    return response;
}

const showErrorToast = (
    errorMessage: string,
    titile?: string) => {

    notification.error({
        message: titile ?? 'Error',
        description: errorMessage,
        placement: "topLeft"
    });
}

function enumerateEnumKey<O extends object, K extends keyof O = keyof O>(obj: O): K[] {
    return Object.keys(obj).filter(k => Number.isNaN(+k)) as K[];
}

function enumerateEnumVal<O>(obj: object): O[] {
    return Object
        .keys(obj)
        .filter(k => Number.isNaN(+k))
        .map(k => (obj as any)[k] as O);
}

const isAllTrue = (objToTest: any, pred: (member: any) => boolean) => {
    if (!objToTest) {
        return false;
    }

    for (let key of Object.keys(objToTest)) {
        if (!pred(objToTest[key])) {
            return false;
        }
    }

    return true;
}

class DataCache<T> {
    private _cachePeriodMs: number;
    private _accessor: () => Promise<T | null | undefined>;
    private _data: T | null | undefined = null;
    private _refreshRequested: boolean = false;
    private _lastRefreshed: number = Date.now();

    public static Create = async <T,>(
        accessor: () => Promise<T | null | undefined>,
        cachePeriodMs: number,
        defaultValue?: T): Promise<DataCache<T>> => {
        const cache = new DataCache<T>(accessor, cachePeriodMs);

        try {
            await cache.refresh(defaultValue);
        } catch { }

        return cache;
    };

    private constructor(
        accessor: () => Promise<T | null | undefined>,
        cachePeriodMs: number) {
        this._cachePeriodMs = cachePeriodMs;
        this._accessor = accessor;
    };

    public readonly refresh = async (data?: T) => {
        if (this._refreshRequested) {
            return;
        }

        this._refreshRequested = true;

        try {
            this._data = data === undefined
                ? await this._accessor()
                : data;
            this._lastRefreshed = Date.now();
        }
        finally {
            this._refreshRequested = false;
        }
    };

    public get data(): T | null | undefined {
        if (this._lastRefreshed + this._cachePeriodMs < Date.now()
            || this._data === null // if last load/refresh was failed
            || this._data === undefined) { // if last load/refresh was failed
            this.refresh();
        }

        return this._data;
    }
}

// cache without auto-refresh
class StaticDataCache<T> {
    private _cachePeriodMs: number;
    private _data: T | undefined = undefined;
    private _lastRefreshed: number = Date.now();
    private _expired: boolean = false;

    public static Create = <T,>(
        cachePeriodMs: number,
        defaultValue?: T): StaticDataCache<T> => new StaticDataCache<T>(cachePeriodMs, defaultValue);

    private constructor(
        cachePeriodMs: number,
        data?: T) {

        this._cachePeriodMs = cachePeriodMs;
        this._data = data;
    };

    public get data(): T | undefined {
        if (this._expired
            || this._lastRefreshed + this._cachePeriodMs < Date.now()) {

            this._expired = true;
            return undefined;
        }

        return this._data;
    }

    public set data(value: T | undefined) {
        this._lastRefreshed = Date.now();
        this._expired = false;
        this._data = value;
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isFunction = (value: any) =>
    !!value
    && (Object.prototype.toString.call(value) === "[object Function]"
        || "function" === typeof value
        || value instanceof Function);

function getRuleObject<T = any>(valiFunc: (rawValue: T) => string | null, isRenderer?: boolean): Rule;
function getRuleObject(valiFunc: (getFieldsValue: () => any) => string | null, isRenderer?: boolean): Rule {
    if (!valiFunc) {
        throw Error("Argument valiFunc is missing");
    }

    isRenderer ??= false;

    if (isRenderer) {
        return ({ getFieldsValue }) => ({
            validator(_, __) {
                const validateResult = valiFunc(getFieldsValue);

                if (validateResult) {
                    return Promise.reject(validateResult);
                }

                return Promise.resolve();
            }
        });
    }

    return {
        validator: (_, value) => {
            const validateResult = valiFunc(value);

            if (validateResult) {
                return Promise.reject(validateResult);
            }

            return Promise.resolve();
        }
    };
};

function getRuleObjectDelayed<T = any>(valiFunc: (rawValue: T) => string | null, timeout?: number, isRenderer?: boolean): Rule;
function getRuleObjectDelayed(valiFunc: (getFieldsValue: () => any) => string | null, timeout?: number, isRenderer?: boolean): Rule {
    if (!valiFunc) {
        throw Error("Argument valiFunc is missing");
    }

    timeout ??= 300;

    if (timeout === 0) {
        return getRuleObject(valiFunc, isRenderer);
    }

    isRenderer ??= false;

    let timer: NodeJS.Timeout | null = null;

    if (isRenderer) {

        return ({ getFieldsValue }) => ({
            validator: (_, __) => {
                if (timer) {
                    clearTimeout(timer);
                }

                return new Promise((resolve, reject) => {
                    timer = setTimeout(() => {
                        const validateResult = valiFunc(getFieldsValue);

                        if (validateResult) {
                            return reject(validateResult);
                        }

                        return resolve(validateResult);
                    }, timeout);
                })
            }
        });
    }

    return {
        validator: (_, value) => {
            if (timer) {
                clearTimeout(timer);
            }

            return new Promise((resolve, reject) => {
                timer = setTimeout(() => {
                    const validateResult = valiFunc(value);

                    if (validateResult) {
                        return reject(validateResult);
                    }

                    return resolve(validateResult);
                }, timeout);
            })
        }
    };
};

const isEnumName = <T,>(str: string, _enum: T): str is Extract<keyof T, string> => {
    if (!str) {
        return false;
    }

    if (str in _enum) {
        return true;
    }

    if (Object.keys(_enum)
        .filter(k => typeof k === 'string')
        .find(k => k.toLowerCase() === str.toLowerCase())) {

        return true;
    }

    return false;
}

const enumFromName = <T,>(name: string, _enum: T) => {
    if (!isEnumName(name, _enum)) throw Error();

    if (_enum[name]) {

        return _enum[name];
    }

    var key = Object.keys(_enum)
        .filter(k => typeof k === 'string')
        .find(k => k.toLowerCase() === name.toLowerCase());

    if (key) {
        return (_enum as any)[key];
    }

    throw Error();
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const clearRecord = <TK extends keyof any, TV>(source: Record<TK, TV>) => {
    for (let key in source) {
        delete source[key];
    }
}

const amountRegex = /^(?<sign>-)?(0*)(?<dpl>\d+)(?<dp>.|,)?(?<dpr>\d{1,2})?(.*)$/;

const amountFormaterRealTime = (e: ChangeEvent<HTMLInputElement>): string => { // real-time
    const execRes = amountRegex.exec(e.target.value);

    const dpl = execRes?.groups?.['dpl']; // decimal-point-left
    const dp = execRes?.groups?.['dp']; // decimal-point
    const dpr = execRes?.groups?.['dpr']; // decimal-point-right

    let result = '';

    dpl && (result = dpl);
    dpl && dp && (result = result + '.')
    dpl && dp && dpr && (result = result + dpr);

    return result;
}

const formatAmount = (input: string, allowNegative?: boolean) => {
    allowNegative ??= false;

    const execRes = amountRegex.exec(input);

    const sign = execRes?.groups?.['sign']; // decimal-point-left
    const dpl = execRes?.groups?.['dpl']; // decimal-point-left
    const dp = execRes?.groups?.['dp']; // decimal-point
    const dpr = execRes?.groups?.['dpr']; // decimal-point-right

    let result = '';

    dpl && (result = dpl);

    dpl && dp && dpr && (result = result + '.' + dpr);

    if (dpl && !(dp && dpr)) {
        result = dpl + '.00';
    }

    if (dpl && dp && dpr && (dpr.length === 1)) {
        result = result + '0';
    }

    if (result && allowNegative && sign) {
        result = '-' + result;
    }

    return result;
}

const amountFormater = (e: FocusEvent<HTMLInputElement>): string => formatAmount(e.target.value);

const base64abc = [
    "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
    "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z",
    "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m",
    "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z",
    "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "/"
];

const base64codes = [
    255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255,
    255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255,
    255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 62, 255, 255, 255, 63,
    52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 255, 255, 255, 0, 255, 255,
    255, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
    15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 255, 255, 255, 255, 255,
    255, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
    41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51
];

function getBase64Code(charCode: number) {
    if (charCode >= base64codes.length) {
        throw new Error("Unable to parse base64 string.");
    }
    const code = base64codes[charCode];
    if (code === 255) {
        throw new Error("Unable to parse base64 string.");
    }
    return code;
}

export function bytesToBase64(bytes: number[] | Uint8Array) {
    let result = '', i, l = bytes.length;
    for (i = 2; i < l; i += 3) {
        result += base64abc[bytes[i - 2] >> 2];
        result += base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
        result += base64abc[((bytes[i - 1] & 0x0F) << 2) | (bytes[i] >> 6)];
        result += base64abc[bytes[i] & 0x3F];
    }
    if (i === l + 1) { // 1 octet yet to write
        result += base64abc[bytes[i - 2] >> 2];
        result += base64abc[(bytes[i - 2] & 0x03) << 4];
        result += "==";
    }
    if (i === l) { // 2 octets yet to write
        result += base64abc[bytes[i - 2] >> 2];
        result += base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
        result += base64abc[(bytes[i - 1] & 0x0F) << 2];
        result += "=";
    }
    return result;
}

export function base64ToBytes(str: string) {
    if (str.length % 4 !== 0) {
        throw new Error("Unable to parse base64 string.");
    }
    const index = str.indexOf("=");
    if (index !== -1 && index < str.length - 2) {
        throw new Error("Unable to parse base64 string.");
    }
    let missingOctets = str.endsWith("==") ? 2 : str.endsWith("=") ? 1 : 0,
        n = str.length,
        result = new Uint8Array(3 * (n / 4)),
        buffer: number;
    for (let i = 0, j = 0; i < n; i += 4, j += 3) {
        buffer =
            (getBase64Code(str.charCodeAt(i)) << 18) |
            (getBase64Code(str.charCodeAt(i + 1)) << 12) |
            (getBase64Code(str.charCodeAt(i + 2)) << 6) |
            getBase64Code(str.charCodeAt(i + 3));
        result[j] = buffer >> 16;
        result[j + 1] = (buffer >> 8) & 0xFF;
        result[j + 2] = buffer & 0xFF;
    }
    return result.subarray(0, result.length - missingOctets);
}

export function base64encode(str: string, encoder: { encode: (str: string) => Uint8Array | number[] } = new TextEncoder()) {
    return bytesToBase64(encoder.encode(str));
}

export function base64decode(str: string, decoder: { decode: (bytes: Uint8Array) => string } = new TextDecoder()) {
    return decoder.decode(base64ToBytes(str));
}

const routeIdRegex = /^\d+$/;

const removeHashAndQueryFromUrl = (url?: string | null) => {

    if (!url) {
        return '';
    }

    let splitted = url.split('/');

    splitted = splitted
        .map(s => routeIdRegex.test(s)
            ? '{id}'
            : s);

    let lastRoute = splitted[splitted.length - 1];

    let lastRouteSplitted = lastRoute.split('#');

    if (lastRouteSplitted.length > 1) {
        splitted[splitted.length - 1] = lastRouteSplitted[0];
    }

    lastRouteSplitted = lastRoute.split('?');

    if (lastRouteSplitted.length > 1) {
        splitted[splitted.length - 1] = lastRouteSplitted[0];
    }

    return splitted.join('/');
}
const dtoDateToFormDate = (source: Moment | string | undefined | null) =>
    source
        ? stringMomentDateToMoment(source)
        : null;

const stringMomentDateToMoment = (source: Moment | string) => {
    let result: Moment;

    if (typeof (source) === 'string') {
        result = moment(source);
    }
    else {
        result = source;
    }

    if (!result.isValid()) {
        return null;
    }

    return moment.utc(result.format(DefaultJsonDateFormat));
}

const ContactNameRule: Rule = {
    pattern: /^[a-zA-Z]+[a-zA-Z0-9._'-/ ]*$/,
    message: "Name is allowed to contain alphanumerics, spaces and [._'-/], and starts with alphabets."
};

const MeterSerialRule: Rule = {
    pattern: /^[a-zA-Z0-9]*$/,
    message: "Meter serial is allowed to contain alphanumerics only."
};

const RegisterIdRule: Rule = {
    pattern: /^[a-zA-Z0-9]*$/,
    message: "Register ID is allowed to contain alphanumerics only."
};

const generateNonDuplicateRule: (
    accessor: (val: any) => string[] | null,
    index: number,
    message: string) => Rule =
    (accessor, index, message) =>
        getRuleObject(getFieldsValue => {
            const allSerials = accessor(getFieldsValue());

            if (!allSerials) {
                return null;
            }

            const selfSerial = allSerials[index];

            if (!selfSerial) {
                return null;
            }

            allSerials.splice(index, 1);

            const dupSerial = allSerials
                .filter(s => !!s)
                .find(s => s.toLowerCase() === selfSerial.toLowerCase());

            return dupSerial
                ? message
                : null;
        }, true);


const generateRequiredRule: (fieldName: string | null, customMessage?: string) => Rule = (fieldName, customMessage) => ({
    required: true,
    message: customMessage ?? `${fieldName} is required.`
});

const generateCustMsgRequiredRule: (msg: string) => Rule = (msg) => ({
    required: true,
    message: msg
});

const generateLengthRule: (fieldName: string, length: number) => Rule = (fieldName, length) => ({
    type: 'string',
    max: length,
    message: `${fieldName} should contain less than ${length} characters.`
});

const generateRangeRule: (fieldName: string, min: number, max: number) => Rule = (fieldName, min, max) => ({
    type: 'number',
    min: min,
    max: max,
    message: `${fieldName} should range from ${min} to ${max}.`
});

const AuMobileRule: Rule = {
    pattern: /^04\d{8}$/,
    message: 'Mobile number must be 10 digits long, starts with 04.'
};

const EmailRule: Rule = {
    type: 'email',
    message: 'Email address is not valid.'
};

const AccountNumberRule: Rule = {
    pattern: /^\d{8}$/,
    message: 'Account number contains 8 digits.'
};

const PostcodeRule: Rule = {
    message: 'Post code is not valid.',
    pattern: /^\d{4}$/
};

const SuburbNameRule: Rule = {
    pattern: /^[0-9a-zA-Z/ ]*$/,
    message: 'Suburb name is not valid.'
}

const VarcharRule: Rule = {
    pattern: /[ -~]*/,
    message: 'Invalid characters found.'
}

const generateDobOver18Rule: (msg: string) => Rule =
    (msg) => getRuleObject<Moment | null>(
        val => {

            if (!val || !val.isValid()) {
                return null;
            }

            if (normalizeDate().diff(normalizeDate(val), 'year') < 18) {
                return msg;
            }

            return null;
        });

const normalizeDate = (val?: Moment) => moment(
    val?.format(DefaultJsonDateFormat) ?? moment().format(DefaultJsonDateFormat));

const generateDateRangeRule: (minMsg: string, maxMsg: string, minOffsetDays?: number, maxOffsetDays?: number) => Rule =
    (minMsg, maxMsg, minOffsetDays, maxOffsetDays) => getRuleObject<Moment | null>(
        val => {

            if (!val || !val.isValid()) {
                return null;
            }

            const valDate = normalizeDate(val);

            let resultMsg = '';

            if (typeof minOffsetDays === 'number'
                && isFinite(minOffsetDays)) {

                if (valDate < normalizeDate().add(minOffsetDays, 'days')) {
                    resultMsg += minMsg;
                }
            }

            if (typeof maxOffsetDays === 'number'
                && isFinite(maxOffsetDays)) {

                if (valDate > normalizeDate().add(maxOffsetDays, 'days')) {
                    resultMsg += resultMsg
                        ? '\n' + maxMsg
                        : maxMsg;
                }
            }

            if (resultMsg) {
                return resultMsg;
            }

            return null;
        });

const generateCheckBoxRule: (msg: string) => Rule =
    (msg) => getRuleObject<boolean>(
        val => !val ? msg : null);

const generateVerificationRule: (codeLen: number) => Rule =
    (codeLen: number) => ({
        pattern: new RegExp(`^\\d{${codeLen}}$`),
        message: `Verification code must be ${codeLen} digits.`
    });

const today = () => moment.utc(moment().format(DefaultJsonDateFormat));

const yearsAgoToday = (years: number) =>
    moment().startOf('day').subtract(years, 'years');

const propOrderedStringify = (source: any) => { // note no loop reference is allowed

    if (!source || !(typeof (source) === 'object')) {
        return '';
    }

    var keys = Object.keys(source).sort();

    return JSON.stringify(
        keys.reduce((p, c) => { (p as any)[c] = source[c]; return p; }, {}));
}

const countdownRecord: any = {}

const getCountdownRecord = (name: string): number => {
    let result: any = countdownRecord[name];

    if (result === undefined) {
        result = localStorage.getItem(name);
    }

    result = Number(result);

    if (Number.isNaN(result)) {
        return 0;
    }

    return result < 0
        ? 0
        : result;
};

function useCountdown(
    countdownName: string,
    countdownLength: number): [number, () => void] {

    if (!countdownName || !countdownLength) {
        throw Error('Non-empty countdownName and non-zero countdownLength is required.');
    }

    const countdownDic = useMemo<any>(() => ({}), []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const internalCountdownName = useMemo(() => `util-countdown-${countdownName}`, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const lastCountDic = useMemo(() => ({ [internalCountdownName]: getCountdownRecord(internalCountdownName) }), []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const timeLeft = useMemo(() => countdownLength - moment().unix() + lastCountDic[internalCountdownName], [])

    const [countdownLeft, setCountLeft] = useState<number>(timeLeft < 0 ? 0 : timeLeft);

    const startCountdown: () => void = () => {

        if (countdownDic[internalCountdownName]) {
            return;
        }

        const now = moment().unix();
        countdownRecord[internalCountdownName] = now;
        localStorage.setItem(internalCountdownName, now.toString());

        lastCountDic[internalCountdownName] = now;

        countdownDic[internalCountdownName] = setInterval(() => {
            const intervalTimeLeft = countdownLength - moment().unix() +
                lastCountDic[internalCountdownName];

            if (intervalTimeLeft <= 0) {
                setCountLeft(0);
                clearInterval(countdownDic[internalCountdownName]);
                delete countdownDic[internalCountdownName];
            }
            else {
                setCountLeft(intervalTimeLeft);
            }
        }, 1000);
    }

    useEffect(() => {

        if (timeLeft > 0) {
            countdownDic[internalCountdownName] = setInterval(() => {
                const intervalTimeLeft = countdownLength - moment().unix() + lastCountDic[internalCountdownName];

                if (intervalTimeLeft <= 0) {
                    setCountLeft(0);
                    clearInterval(countdownDic[internalCountdownName]);
                    delete countdownDic[internalCountdownName];
                }
                else {
                    setCountLeft(intervalTimeLeft);
                }
            }, 1000);
        }

        return () => {
            if (!countdownDic[internalCountdownName]) {
                return;
            }

            clearInterval(countdownDic[internalCountdownName]);
            delete countdownDic[internalCountdownName];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [countdownLeft, startCountdown];
};

const workoutCountDownTimeStr = (remainSec: number) => {
    if (remainSec <= 0) {
        return '';
    }

    let result = '';

    const flo = Math.floor(remainSec / 60);
    const mod = remainSec % 60;

    if (flo > 0) {
        result += `${flo} mins `;
    }

    result += `${mod}s`;

    return result;
}

const workoutCountDownMessage = (remainSec: number) => {
    if (remainSec <= 0) {
        return '';
    }

    return ` (after ${workoutCountDownTimeStr(remainSec)})`;
}

const getCountdownName = (type: VerificationCodeType, purpose: VerificationCodePurpose) => type + purpose;

const getServiceType = (nmi: string) =>
    nmi?.startsWith('5')
        ? ServiceType.Gas
        : ServiceType.Power;

const getServiceIcon = (nmi: string, style?: CSSProperties) =>
    <FontAwesomeIcon
        icon={getServiceType(nmi) === ServiceType.Gas ? faFire : faBolt}
        style={style} />;

const getServiceTypeIcon = (svcType: ServiceType, style?: CSSProperties) =>
    <FontAwesomeIcon
        icon={svcType === ServiceType.Gas ? faFire : faBolt}
        style={style} />;

const getServiceAbrev = (nmi: string) =>
    getServiceType(nmi) === ServiceType.Gas
        ? 'Gas'
        : 'Pow';

const encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
    let base64 = '';

    const bytes = new Uint8Array(arrayBuffer)
    const byteLength = bytes.byteLength
    const byteRemainder = byteLength % 3
    const mainLength = byteLength - byteRemainder

    let a: number, b: number, c: number, d: number;
    let chunk;

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i = i + 3) {
        // Combine the three bytes into a single integer
        chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]

        // Use bitmasks to extract 6-bit segments from the triplet
        a = (chunk & 16515072) >> 18 // 16515072 = (2^6 - 1) << 18
        b = (chunk & 258048) >> 12 // 258048   = (2^6 - 1) << 12
        c = (chunk & 4032) >> 6 // 4032     = (2^6 - 1) << 6
        d = chunk & 63               // 63       = 2^6 - 1

        // Convert the raw binary segments to the appropriate ASCII encoding
        base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d]
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder === 1) {
        chunk = bytes[mainLength]

        a = (chunk & 252) >> 2 // 252 = (2^6 - 1) << 2

        // Set the 4 least significant bits to zero
        b = (chunk & 3) << 4 // 3   = 2^2 - 1

        base64 += encodings[a] + encodings[b] + '=='
    } else if (byteRemainder === 2) {
        chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1]

        a = (chunk & 64512) >> 10 // 64512 = (2^6 - 1) << 10
        b = (chunk & 1008) >> 4 // 1008  = (2^6 - 1) << 4

        // Set the 2 least significant bits to zero
        c = (chunk & 15) << 2 // 15    = 2^4 - 1

        base64 += encodings[a] + encodings[b] + encodings[c] + '='
    }

    return base64
}

const blobToBase64 = async (source: Blob) => {
    const reader = new FileReader();

    const result = await new Promise<string>((res) => {
        reader.onloadend = () =>
            res(reader.result as string);

        reader.readAsDataURL(source);
    });

    return result.substring(23); // remove data url header
}

const days18YrsAgo = moment()
    .startOf('day')
    .diff(
        moment().startOf('day').add(-18, 'years'),
        'days');

const dobAbove18 = (date: Moment) =>
    shouldBeDisabledByDayOffset(date, undefined, -days18YrsAgo);

let cachedDateForDisableComparison: { [key: number]: number } = {}; // will break the logic if the current date is changed

setInterval(() => cachedDateForDisableComparison = {}, 60000);

const secondsADay = 86400;

const currentTzOffsetMinute = new Date().getTimezoneOffset();
const currentTzOffset = -currentTzOffsetMinute * 60;
const currentTzOffsetHour = currentTzOffset / 3600;

const timezoneOffsetAEST = -600;

const getUtcOffsetSec = (date: Moment) => date.utcOffset() * 60;

const shouldBeDisabledByDayOffset = (date: Moment, min?: number, max?: number) => {
    const timezoneOffset = date.toDate().getTimezoneOffset() * 60; // in seconds
    const val = toUtcStartOfDayTs(date) + timezoneOffset; // startOf the day in UTC

    if (min !== undefined) {
        const minThs = (cachedDateForDisableComparison[min] ??=
            moment().startOf('day').add(min, 'days').unix());

        if (val < minThs) {
            return true;
        }
    }

    if (max !== undefined) {
        const maxThs = (cachedDateForDisableComparison[max] ??=
            moment().startOf('day').add(max, 'days').unix());

        if (val > maxThs) {
            return true;
        }
    }

    return false;
}

const toUtcStartOfDayTs = (date: Moment) => Math.floor((date.unix() + getUtcOffsetSec(date)) / secondsADay) * secondsADay;
const toUtcStartOfDay = (date: Moment) => moment.unix(toUtcStartOfDayTs(date)).utc();

// min and max must be startOf day in UTC
const shouldBeDisabledByDayDateRange = (date: Moment, min?: Moment | null, max?: Moment | null) => {
    const val = toUtcStartOfDayTs(date); // utc startOf the day

    if (min && val < min.unix()) {
        return true;
    }

    if (max && val > max.unix()) {
        return true;
    }

    return false;
}

const getValidBasicMeters = (source: MeterDto[] | null): MeterDto[] => {
    if (!source || source.length === 0) {
        return [];
    }

    return source
        .filter(m => m.meterType === MeterType.GasBasic
            || m.meterType === MeterType.GasHotWater
            || m.meterType === MeterType.PowerBasic)
        .map(m => m.registers
            ? {
                ...m,
                registers: m.registers.filter(r => r.registerType === MeterType.PowerBasic)
            }
            : { ...m })
        .filter(m => m.meterType !== MeterType.PowerBasic
            || (m.registers && m.registers.length > 0));
}

const toSite = (svcs: AccountServiceInfo[] | null): Site[] => {
    if (!svcs?.length) {
        return [];
    }

    return svcs.map(svc => ({
        nmi: svc.siteIdentifier,
        address: svc.siteAddress,
        accountServiceInfo: svc
    }));
}

const aestOffset = 10;

const fromAestToLocalTimeZone = (date: Moment) => { // note this function will not regard/change utcOffset, hence it's only for formatting purposes

    const hoursToOffset = currentTzOffsetHour - aestOffset;

    return date.add(hoursToOffset, 'hour');
}

// currentTzOffsetMinute = utc - currentTz
// currentTz - aest = (utc-aest) - (utc - currentTz)
const getTimeZoneOffsetToAEST = () => {
    return timezoneOffsetAEST - currentTzOffsetMinute;
}

const isSmartMeterSite = (meters: MeterDto[]) =>
    meters.some(m => m.meterType === MeterType.PowerInterval);

const toSelfReadUploadList = (source: SelfReadUploadReview[]): ListSelfReadUpload[] => {
    return source
        .map(u => {
            const groupReads: { [key: string]: SelfReadReview[] } = {};

            u.selfReads.forEach(r => {
                groupReads[r.meterSerial] ??= [];
                groupReads[r.meterSerial] = [...groupReads[r.meterSerial], r]
            });

            return {
                readDate: moment(u.selfReadDate).format(DefaultDisplayDateFormat),
                readReason: u.selfReadReason,
                readNote: u.selfReadNote,
                approvalStatus: u.approvalStatus,
                reviewReason: u.reviewReason,
                reviewNote: u.reviewNote,
                uploadDateTime: fromAestToLocalTimeZone(moment(u.uploadDateTime)).format(`HH:mm:ss ${DefaultDisplayDateFormat}`),
                nmi: u.nmi,
                accountServiceId: u.accountServiceId,
                selfReadUploadId: u.selfReadUploadId,
                reads: Object.keys(groupReads).map(k => {
                    // if(groupReads[k].length)
                    const refGroup = groupReads[k];
                    const refRead = refGroup[0]!;

                    return {
                        selfReadId: refRead.selfReadId,
                        meterSerial: refRead.meterSerial,
                        accpetedMeterSerial: refRead.actualMeterSerial,
                        readIndex: refGroup.length === 1
                            ? refRead.readIndex
                            : null,
                        accpetedReadIndex: refGroup.length === 1
                            ? refRead.actualReadIndex
                            : null,
                        readPhoto: refGroup.length === 1
                            ? refRead.photoDocuments[0]
                            : null,
                        isMultiRegister: refGroup.length !== 1,
                        registerReads: refGroup.length === 1
                            ? null
                            : refGroup.map(r => ({
                                registerId: r.registerId,
                                accpetedRegisterId: r.actualRegisterId,
                                registerIndex: r.readIndex,
                                accpetedRegisterIndex: r.actualReadIndex,
                                registerReadPhoto: r.photoDocuments[0],
                                approvalStatus: r.approvalStatus,
                                reviewReason: r.reviewReason,
                                selfReadId: r.selfReadId
                            })),
                        approvalStatus: refGroup.length === 1
                            ? refRead.approvalStatus
                            : null,
                        reviewReason: refGroup.length === 1
                            ? refRead.reviewReason
                            : null
                    };
                })
            };
        });
}

const removeFromQuery = (query: ParsedQuery, keysToRemove: string[]) => {
    const otherKeys = Object
        .keys(query)
        .filter(k => keysToRemove.findIndex(r => r === k) < 0);

    if (otherKeys.length === 0) {
        return '';
    }

    const otherKeysObj = otherKeys
        .reduce((p, c) => {
            p[c] = query[c];
            return p;
        }, {} as any);

    return `?${stringify(otherKeysObj)}`;
}

const genJsonHeaderWithHCaptchaToken = (hCaptchaToken: string | null) => ({
    ...JsonHeader,
    ...(hCaptchaToken
        ? { [HCaptchaTokenHeaderName]: hCaptchaToken }
        : undefined)
});

class RequestSameOrigin extends Request {
    constructor(input: RequestInfo, init?: RequestInit) {
        const impersonateToken = sessionStorage.getItem(ImpersonateTokenName);

        super(input,
            {
                ...init,
                credentials: 'same-origin',
                headers: {
                    ...init?.headers,
                    ...(impersonateToken && { Authorization: `Bearer ${impersonateToken}` })
                }
            });
    }
}

const ellipsisString = (
    self: string,
    targetLength: number,
    keepEndLength: number = 0,
    holder: string = '...') => {
    if (self.length <= targetLength || holder.length >= self.length) return self;

    let tailLength = holder.length + keepEndLength;
    if (tailLength > self.length) keepEndLength = self.length - holder.length;
    if (tailLength > targetLength) keepEndLength = targetLength - holder.length;

    return self.slice(0, targetLength - tailLength)
        + holder
        + (keepEndLength > 0 ? self.slice(self.length - keepEndLength) : '');
}

const PendingTransferStatuses = [
    EuropaAccountServiceStatus.PendingMoveIn,
    EuropaAccountServiceStatus.PendingSwitch,
    EuropaAccountServiceStatus.PendingSwitchResults,
    EuropaAccountServiceStatus.TransferObjected,
    EuropaAccountServiceStatus.TransferCancelled,
    EuropaAccountServiceStatus.TransferPending,
    EuropaAccountServiceStatus.TransferRejected,
    EuropaAccountServiceStatus.TransferRequested
];

const PendingMoveOutStatuses = [EuropaAccountServiceStatus.PendingSwitchOut];

const AllowSelfReadUploadStatuses = [EuropaAccountServiceStatus.Switched];

const getAccountServiceMeters = (accntMtrs: AccountMeterDto[], accountServiceId: number) =>
    accntMtrs.filter(am => am.accountServiceIds.some(acsId => acsId === accountServiceId));

const isPendingTransferStatus = (s: EuropaAccountServiceStatus) => PendingTransferStatuses.some(ss => s === ss);
const isPendingMoveOutStatus = (s: EuropaAccountServiceStatus) => PendingMoveOutStatuses.some(ss => s === ss);
const isAllowUploadSelfReadStatus = (s: EuropaAccountServiceStatus) => AllowSelfReadUploadStatuses.some(ss => s === ss);

const anySmartMeters = (mtrs: AccountMeterDto[]) =>
    mtrs.length && mtrs.some(
        am => am.meterType === MeterType.PowerInterval
            || am.meterType === MeterType.GasInterval);

const disabledCallbackDates = (currentDate: Moment, publicHolidays: string[]) => {
    return currentDate < moment().endOf('day')
        || currentDate > moment().add(7, 'days').endOf('day')
        || isHoliday(currentDate, publicHolidays, [0,6], false);
};

export {
    formatDisplayDate,
    formatTime,
    cloneObject,
    handleResponse,
    handleDownlodResponse,
    enumerateEnumKey,
    enumerateEnumVal,
    isMobileAndTablet,
    getDateFromString,
    getUTCDate,
    isAllTrue,
    getRuleObject,
    DataCache,
    delay,
    isEnumName,
    enumFromName,
    getRuleObjectDelayed,
    showErrorToast,
    clearRecord,
    amountFormater,
    amountFormaterRealTime,
    formatAmount,
    removeHashAndQueryFromUrl,
    RequestSameOrigin,
    dtoDateToFormDate,
    stringMomentDateToMoment,
    ContactNameRule,
    generateRequiredRule,
    AuMobileRule,
    EmailRule,
    generateDobOver18Rule,
    today,
    yearsAgoToday,
    generateCheckBoxRule,
    propOrderedStringify,
    useCountdown,
    SuburbNameRule,
    PostcodeRule,
    generateVerificationRule,
    generateLengthRule,
    VarcharRule,
    formatJsonDate,
    workoutCountDownTimeStr,
    workoutCountDownMessage,
    getCountdownName,
    AccountNumberRule,
    getServiceAbrev,
    getServiceIcon,
    getServiceType,
    ServiceType,
    arrayBufferToBase64,
    blobToBase64,
    generateCustMsgRequiredRule,
    dobAbove18,
    generateDateRangeRule,
    MeterSerialRule,
    RegisterIdRule,
    generateNonDuplicateRule,
    getValidBasicMeters,
    toSite,
    isSmartMeterSite,
    toSelfReadUploadList,
    generateRangeRule,
    normalizeDate,
    shouldBeDisabledByDayOffset,
    removeFromQuery,
    fromAestToLocalTimeZone,
    genJsonHeaderWithHCaptchaToken,
    StaticDataCache,
    shouldBeDisabledByDayDateRange,
    toUtcStartOfDay,
    ellipsisString,
    isPendingTransferStatus,
    isPendingMoveOutStatus,
    isAllowUploadSelfReadStatus,
    getAccountServiceMeters,
    anySmartMeters,
    getServiceTypeIcon,
    AllSelfReadReasons,
    AllContactTitles,
    AllStateCodes,
    AllDeliveryTypes,
    AllSelfReadUploadReviewReason,
    AllSelfReadReviewReason,
    getTimeZoneOffsetToAEST,
    disabledCallbackDates,
    startSpinner,
    stopSpinner
};
